import type { QueryClient } from '@tanstack/react-query';
import type {
  IGetToolResponse,
  ITool,
  IToolApiAction,
  IToolApiConfig,
  IToolAuthConfig,
  IToolComposioConfig,
  IToolOpenConnectorConfig,
  IToolDetails,
  IToolMcpAction,
  IToolMcpConfig,
  IUpdateAPIToolPayload,
  IUpdateMCPServerPayload,
  ToolStatus,
  ToolType,
} from 'features/chat/agent-chat-module/services/gpt-chat.service';
import {
  DEFAULT_RESOURCE_VISIBILITY,
  type ResourceVisibility,
} from 'types/visibility';
import type { OwnedResource } from 'lib/ownership';

type ToolVisibilitySource = {
  visibility?: ResourceVisibility | string | null;
  Visibility?: ResourceVisibility | string | null;
};

export const getToolVisibility = (tool?: ToolVisibilitySource | null): ResourceVisibility => {
  const value = tool?.visibility ?? tool?.Visibility;
  return value === 'private' || value === 'public' ? value : DEFAULT_RESOURCE_VISIBILITY;
};

export const getToolTypeLabel = (type?: ToolType) => {
  if (type === 'mcp_server') return 'MCP Server';
  if (type === 'composio') return 'Composio';
  if (type === 'open_connector') return 'Open Connector';
  if (type === 'webhook') return 'Webhook';
  if (type === 'graphql') return 'GraphQL';
  return 'Tool';
};

export const getStatusVariant = (status?: ToolStatus) => {
  if (status === 'active') return 'default';
  if (status === 'maintenance') return 'secondary';
  return 'outline';
};

/** Resolve owner id from any casing the tools API / list payload may use. */
const resolveToolCreatedBy = (
  ...sources: Array<{ created_by?: string | null; CreatedBy?: string | null } | null | undefined>
): string | undefined => {
  for (const source of sources) {
    const value = source?.created_by ?? source?.CreatedBy;
    if (value) return value;
  }
  return undefined;
};

type ToolEditSource = {
  created_by?: string | null;
  CreatedBy?: string | null;
  can_edit?: boolean | null;
  CanEdit?: boolean | null;
};

/** Server-computed write access from any casing the tools API may use. */
const resolveToolCanEdit = (
  ...sources: Array<ToolEditSource | null | undefined>
): boolean | null => {
  for (const source of sources) {
    const value = source?.can_edit ?? source?.CanEdit;
    if (value != null) return value;
  }
  return null;
};

/** Owner id from a tool detail response (nested or root) or list/fallback object. */
export const getToolCreatedBy = (
  response?: IGetToolResponse | null,
  fallback?: ToolEditSource | null
): string | undefined =>
  resolveToolCreatedBy(response?.tool, response?.data, response, fallback);

/**
 * Edit-rights subject for `useIsOwner` / `canEditResource`: carries the server
 * `can_edit` flag when present, else just the owner id for the legacy fallback.
 */
export const getToolEditSubject = (
  response?: IGetToolResponse | null,
  fallback?: ToolEditSource | null
): OwnedResource => ({
  created_by: getToolCreatedBy(response, fallback),
  can_edit: resolveToolCanEdit(response?.tool, response?.data, response, fallback),
});

export const normalizeToolDetails = (
  response?: IGetToolResponse | null,
  fallbackTool?: ITool | null
): IToolDetails | null => {
  const tool = response?.tool ?? response?.data;
  if (tool) {
    const tutorial = extractTutorialContent(tool);
    const visibility = getToolVisibility(tool);
    const createdBy = resolveToolCreatedBy(tool, response, fallbackTool);
    const canEdit = resolveToolCanEdit(tool, response, fallbackTool);
    return {
      ...tool,
      can_edit: canEdit,
      CanEdit: canEdit,
      visibility,
      Visibility: tool.Visibility ?? tool.visibility ?? visibility,
      base_url: tool.base_url || getToolBaseUrl(tool),
      tutorial: tutorial || tool.tutorial,
      Tutorial: tutorial || tool.Tutorial,
      created_by: createdBy,
      CreatedBy: createdBy,
      ingest_report: tool.ingest_report ?? tool.IngestReport ?? response?.ingest_report ?? response?.IngestReport,
    };
  }

  if (!response && fallbackTool) {
    const createdBy = resolveToolCreatedBy(fallbackTool);
    const canEdit = resolveToolCanEdit(fallbackTool);
    return {
      ...fallbackTool,
      created_by: createdBy,
      CreatedBy: createdBy,
      can_edit: canEdit,
      CanEdit: canEdit,
    };
  }

  const id = response?._id || response?.id || fallbackTool?.id;
  const name = response?.Name || response?.name || fallbackTool?.name;

  if (!id || !name) {
    return fallbackTool || null;
  }

  const tutorial = extractTutorialContent(response);
  const visibility = getToolVisibility(response);
  const createdBy = resolveToolCreatedBy(response, fallbackTool);
  const canEdit = resolveToolCanEdit(response, fallbackTool);

  return {
    id,
    name,
    type: response?.Type || response?.type || fallbackTool?.type || 'api',
    status: response?.Status || response?.status || fallbackTool?.status || 'inactive',
    description: response?.Description || response?.description || fallbackTool?.description || '',
    visibility,
    Visibility: response?.Visibility ?? response?.visibility ?? visibility,
    total_uses: response?.TotalUses || response?.total_uses || fallbackTool?.total_uses || 0,
    total_actions: response?.TotalActions || response?.total_actions || fallbackTool?.total_actions || 0,
    is_open_tool: response?.IsOpenTool ?? response?.is_open_tool ?? fallbackTool?.is_open_tool ?? false,
    base_url: response?.base_url || getToolBaseUrl(response as IToolDetails),
    APIConfig: response?.APIConfig || response?.api_config,
    api_config: response?.api_config || response?.APIConfig,
    MCPConfig: response?.MCPConfig,
    ComposioConfig: response?.ComposioConfig || response?.composio_config,
    composio_config: response?.composio_config || response?.ComposioConfig,
    OpenConnectorConfig: response?.OpenConnectorConfig || response?.open_connector_config,
    open_connector_config: response?.open_connector_config || response?.OpenConnectorConfig,
    IsOpenTool: response?.IsOpenTool ?? response?.is_open_tool ?? fallbackTool?.is_open_tool,
    IsBlocksAuthenticated: response?.IsBlocksAuthenticated,
    RequiresApproval: response?.RequiresApproval,
    Tutorial: tutorial || response?.Tutorial || response?.tutorial,
    tutorial: tutorial || response?.tutorial || response?.Tutorial,
    created_by: createdBy,
    CreatedBy: createdBy,
    can_edit: canEdit,
    CanEdit: canEdit,
    CreatedDate: response?.CreatedDate,
    created_date: response?.created_date,
    LastUpdatedDate: response?.LastUpdatedDate,
    last_updated_date: response?.last_updated_date,
    ingest_report: response?.ingest_report ?? response?.IngestReport,
  };
};

export const getToolIngestReport = (tool?: IToolDetails | null) =>
  tool?.ingest_report ?? tool?.IngestReport;

export const getToolApiConfig = (tool?: IToolDetails | null): IToolApiConfig | undefined =>
  tool?.APIConfig || tool?.api_config;

export const getComposioConfig = (
  tool?: (IToolDetails & { ComposioConfig?: IToolComposioConfig; composio_config?: IToolComposioConfig }) | null
): {
  toolkits: string[];
  tools: string[] | null;
  limit: number | null;
  authConfigIds: Record<string, string>;
} => {
  const cfg = tool?.ComposioConfig || tool?.composio_config;
  return {
    toolkits: cfg?.Toolkits ?? cfg?.toolkits ?? [],
    tools: cfg?.Tools ?? cfg?.tools ?? null,
    limit: cfg?.Limit ?? cfg?.limit ?? null,
    authConfigIds: cfg?.AuthConfigIds ?? cfg?.auth_config_ids ?? {},
  };
};

export const getOpenConnectorConfig = (
  tool?: (IToolDetails & {
    OpenConnectorConfig?: IToolOpenConnectorConfig;
    open_connector_config?: IToolOpenConnectorConfig;
  }) | null
): {
  services: string[];
  actions: string[] | null;
  limit: number | null;
} => {
  const cfg = tool?.OpenConnectorConfig || tool?.open_connector_config;
  return {
    services: cfg?.Services ?? cfg?.services ?? [],
    actions: cfg?.Actions ?? cfg?.actions ?? null,
    limit: cfg?.Limit ?? cfg?.limit ?? null,
  };
};

/** Resolves base URL from API config (supports PascalCase and snake_case API shapes). */
export const getToolBaseUrl = (tool?: IToolDetails | null): string => {
  const apiConfig = getToolApiConfig(tool);
  const fromConfig = apiConfig?.BaseURL || apiConfig?.base_url || '';
  if (fromConfig) return fromConfig;

  const root = tool as (IToolDetails & { base_url?: string }) | null | undefined;
  return root?.base_url || '';
};

export const getToolIsOpenTool = (tool?: IToolDetails | null): boolean =>
  Boolean(tool?.is_open_tool ?? tool?.IsOpenTool ?? false);

export const getToolIsBlocksAuthenticated = (tool?: IToolDetails | null): boolean =>
  Boolean(tool?.IsBlocksAuthenticated ?? (tool as { is_blocks_authenticated?: boolean })?.is_blocks_authenticated);

/** Body for POST /api/tools/api (Save Api Tool) — full payload required when updating status. */
export const buildUpdateAPIToolPayload = (
  detail: IToolDetails,
  projectKey: string,
  status: ToolStatus
): IUpdateAPIToolPayload => ({
  id: detail.id,
  name: detail.name,
  description: detail.description || '',
  base_url: getToolBaseUrl(detail),
  project_key: projectKey,
  visibility: getToolVisibility(detail),
  is_open_tool: getToolIsOpenTool(detail),
  is_blocks_authenticated: getToolIsBlocksAuthenticated(detail),
  status,
});

/** POST /tools/mcp-server — same shape as API update minus base_url. */
export const buildUpdateMCPServerPayload = (
  detail: IToolDetails,
  projectKey: string,
  status: ToolStatus
): IUpdateMCPServerPayload => ({
  id: detail.id,
  name: detail.name,
  description: detail.description || '',
  project_key: projectKey,
  visibility: getToolVisibility(detail),
  is_open_tool: getToolIsOpenTool(detail),
  is_blocks_authenticated: getToolIsBlocksAuthenticated(detail),
  status,
});

export const getToolAuthConfig = (tool?: IToolDetails | null): IToolAuthConfig | undefined =>
  getToolApiConfig(tool)?.AuthConfig;

export const isAutoAuthTool = (tool?: IToolDetails | null): boolean => {
  const authConfig = getToolAuthConfig(tool);
  if (!authConfig) return false;

  const record = authConfig as IToolAuthConfig & { auth_type?: string };
  return (authConfig.AuthType || record.auth_type) === 'auto';
};

export const isOsSessionAuthTool = (tool?: IToolDetails | null): boolean => {
  const authConfig = getToolAuthConfig(tool);
  if (!authConfig) return false;

  const record = authConfig as unknown as Record<string, unknown>;
  return (record.AutoSubType || record.auto_sub_type) === 'os_session';
};

export const AUTO_AUTH_NOT_CONNECTED_MESSAGE = "Not connected: Connect this tool.";

/** Reads tutorial markdown from tool detail or dedicated tutorial API responses. */
export const extractTutorialContent = (source: unknown): string => {
  if (typeof source === 'string') return source;
  if (!source || typeof source !== 'object') return '';

  const record = source as Record<string, unknown>;
  const direct =
    record.tutorial ??
    record.Tutorial ??
    record.tutorial_content ??
    record.TutorialGuide ??
    record.tutorial_guide;

  if (typeof direct === 'string') return direct;

  if (direct && typeof direct === 'object') {
    const nested = direct as Record<string, unknown>;
    if (typeof nested.content === 'string') return nested.content;
    if (typeof nested.markdown === 'string') return nested.markdown;
  }

  const nestedTool = record.tool ?? record.data;
  if (nestedTool && nestedTool !== source) {
    return extractTutorialContent(nestedTool);
  }

  return '';
};

export const getToolTutorial = (tool?: IToolDetails | null): string => extractTutorialContent(tool);

/** Parses agent count from GET /api/tools/{toolId}/agent-count response. */
export const extractToolAgentCount = (response?: unknown): number => {
  if (!response || typeof response !== 'object') return 0;

  const record = response as Record<string, unknown>;
  const data = record.data;

  const candidates = [
    record.count,
    record.agent_count,
    record.total_count,
    typeof data === 'number' ? data : undefined,
    data && typeof data === 'object' ? (data as Record<string, unknown>).count : undefined,
    data && typeof data === 'object' ? (data as Record<string, unknown>).agent_count : undefined,
    data && typeof data === 'object' ? (data as Record<string, unknown>).total_count : undefined,
  ];

  const match = candidates.find((value) => typeof value === 'number');
  return typeof match === 'number' ? match : 0;
};

/** Keeps tool detail query cache in sync after authentication saves. */
export const patchToolAuthConfigInCache = (
  queryClient: QueryClient,
  toolId: string,
  projectKey: string,
  authConfig: IToolAuthConfig,
  headers?: Record<string, string>
) => {
  queryClient.setQueryData<IGetToolResponse>(['tools', 'detail', toolId, projectKey], (old) => {
    if (!old) return old;

    const patchApiConfig = (apiConfig?: IToolApiConfig): IToolApiConfig => ({
      ...apiConfig,
      AuthConfig: authConfig,
      ...(headers !== undefined ? { Headers: headers } : {}),
    });

    const patchNested = (item: IToolDetails): IToolDetails => {
      const existingApiConfig = item.APIConfig || item.api_config;
      const updatedApiConfig = patchApiConfig(existingApiConfig);
      return {
        ...item,
        APIConfig: updatedApiConfig,
        api_config: updatedApiConfig,
      };
    };

    return {
      ...old,
      tool: old.tool ? patchNested(old.tool) : old.tool,
      data: old.data ? patchNested(old.data) : old.data,
    };
  });
};

/** Keeps tool detail and tutorial query caches in sync after tutorial saves. */
export const patchToolTutorialInCache = (
  queryClient: QueryClient,
  toolId: string,
  projectKey: string,
  tutorial: string
) => {
  const tutorialPatch = { tutorial, Tutorial: tutorial };

  queryClient.setQueryData(['tools', 'tutorial', toolId, projectKey], tutorialPatch);

  queryClient.setQueryData<IGetToolResponse>(['tools', 'detail', toolId, projectKey], (old) => {
    if (!old) return old;

    const patchNested = (item: IToolDetails) => ({ ...item, ...tutorialPatch });

    return {
      ...old,
      ...tutorialPatch,
      tool: old.tool ? patchNested(old.tool) : old.tool,
      data: old.data ? patchNested(old.data) : old.data,
    };
  });
};

export const resolveActionMethod = (action: IToolApiAction & { method?: string }): string => {
  const raw = action.Method ?? action.method;
  if (typeof raw !== 'string') return 'API';

  const normalized = raw.trim().toUpperCase();
  return normalized || 'API';
};

export const getHttpMethodBadgeClasses = (method?: string) => {
  switch ((method || '').toUpperCase()) {
    case 'GET':
      return 'bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300';
    case 'POST':
      return 'bg-green-100 text-green-700 dark:bg-green-950 dark:text-green-300';
    case 'PUT':
      return 'bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300';
    case 'DELETE':
      return 'bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300';
    case 'GRAPHQL':
      return 'bg-pink-100 text-pink-700 dark:bg-pink-950 dark:text-pink-300';
    default:
      return 'bg-muted text-muted-foreground';
  }
};

export const getToolActions = (tool?: IToolDetails | null): Array<IToolApiAction & { actionId: string }> => {
  const actions = getToolApiConfig(tool)?.Actions || {};

  return Object.entries(actions).map(([actionId, action]) => {
    const normalizedAction = action as IToolApiAction & { method?: string };

    return {
      actionId,
      ...normalizedAction,
      Method: resolveActionMethod(normalizedAction),
    };
  });
};

export const getMcpConfig = (tool?: IToolDetails | null): IToolMcpConfig | undefined => tool?.MCPConfig;

export const getMcpActions = (tool?: IToolDetails | null): Array<IToolMcpAction & { actionId: string }> => {
  const actions = getMcpConfig(tool)?.Actions || {};

  return Object.entries(actions).map(([actionId, action]) => ({
    actionId,
    ...action,
  }));
};
